import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { api, ApiError } from '../api.js';
import AppHeader from '../components/AppHeader.js';
import CompetitionFlag from '../components/CompetitionFlag.js';
import CoverImageUploader from '../components/CoverImageUploader.js';
import Avatar from '../components/Avatar.js';
import GuestUpgradePanel from '../components/GuestUpgradePanel.js';
import { useActor } from '../lib/useActor.js';
import styles from './GroupScreen.module.css';

/** 8MB, matching `MAX_GROUP_IMAGE_UPLOAD_BYTES` server-side. */
const MAX_COVER_IMAGE_BYTES = 8 * 1024 * 1024;

/**
 * A circle's home (PLAN.md Phase 10.19): the competitions inside it, then
 * (for the organizer) how to get more people in, then who's actually here.
 * That order matches what each visitor needs most — competitions are the
 * everyday destination, sharing is the organizer's most common next step
 * right after creating a fresh circle, and the roster is the thing people
 * check least often.
 *
 * This used to be two screens (`GroupMembersScreen` carried sharing and the
 * roster), split apart because the combined page felt long. Reunited here
 * because a brand-new circle landing on an "invite people" screen, with the
 * competitions it was actually created for a click away, tested worse than
 * one longer page in the order people actually want it.
 *
 * Deleting the whole circle is the least common, most consequential action
 * on this page, so its "danger zone" panel sits last.
 */
export default function GroupScreen() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { isGuest } = useActor();
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [poolImageError, setPoolImageError] = useState<string | null>(null);
  const poolImageInputRef = useRef<HTMLInputElement>(null);
  const poolImageTargetRef = useRef<string | null>(null);

  const {
    data: group,
    isPending,
    isError,
  } = useQuery({ queryKey: ['group', id], queryFn: () => api.getGroup(id) });

  const {
    data: pools,
    isPending: poolsPending,
    isError: poolsError,
  } = useQuery({ queryKey: ['pools', id], queryFn: () => api.listPools(id) });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['group', id] });
    await queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const deletePool = useMutation({
    mutationFn: (poolId: string) => api.deletePool(id, poolId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['pools', id] });
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });

  const updateImage = useMutation({
    mutationFn: (imageUrl: string | null) => api.updateGroupImage(id, imageUrl),
    onSuccess: refresh,
  });

  const updatePoolImage = useMutation({
    mutationFn: ({ poolId, file }: { poolId: string; file: File }) =>
      api.uploadGroupImage(file).then(({ url }) => api.updateBolaoImage(id, poolId, url)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['pools', id] });
    },
    onError: (err) => {
      setPoolImageError(err instanceof ApiError ? err.message : t('coverImage.uploadFailed'));
    },
  });

  const regenerate = useMutation({
    mutationFn: () => api.regenerateJoinLink(id),
    onSuccess: async () => {
      // The new token comes back on the group query itself (`joinToken` is
      // stored in plain text and always readable — see api.ts), so a
      // refetch is enough; no need to hold it in local state.
      setCopied(false);
      await refresh();
    },
  });

  const invite = useMutation({
    mutationFn: () => api.inviteByEmail(id, inviteEmail.trim()),
    onSuccess: async () => {
      setInviteEmail('');
      await refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: (inviteId: string) => api.revokeInvite(id, inviteId),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (memberId: string) => api.removeCircleMember(id, memberId),
    onSuccess: refresh,
  });

  const resetPin = useMutation({
    mutationFn: (memberId: string) => api.resetGuestPin(id, memberId),
    onSuccess: refresh,
  });

  const deleteGroup = useMutation({
    mutationFn: () => api.deleteGroup(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
      void navigate('/');
    },
  });

  if (isPending) {
    return (
      <main className="screen">
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  if (isError || !group) {
    return (
      <main className="screen">
        <Link to="/" className="muted">
          {t('common.back')}
        </Link>
        <p className="error">{t('common.somethingWentWrong')}</p>
      </main>
    );
  }

  // Stored in plain text server-side (Google Docs/Sheets-style), so this is
  // always the current link, not just whatever the last regenerate call
  // happened to return — a page refresh doesn't lose it.
  const joinUrl = group.joinToken ? `${window.location.origin}/join/${group.joinToken}` : null;

  return (
    <main className="screen">
      <AppHeader groupId={id} />
      {/* A guest belongs to this one circle, so "/" redirects straight back
          here — a link that visibly does nothing. Account holders get their
          groups list, which is a real destination. */}
      {isGuest ? null : (
        <Link to="/" className="muted">
          {t('common.back')}
        </Link>
      )}

      <div className={styles.groupHead}>
        {group.isOrganizer ? (
          <CoverImageUploader
            imageUrl={group.imageUrl}
            onChange={(imageUrl) => updateImage.mutate(imageUrl)}
            disabled={updateImage.isPending}
          />
        ) : group.imageUrl ? (
          <img src={group.imageUrl} alt="" className={styles.groupImage} />
        ) : null}
        <h1>{group.name}</h1>
      </div>

      <section>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle} style={{ marginBottom: 0 }}>
            {t('pool.circlePools')}
          </h2>
          <span className={styles.sectionHint}>{t('pool.circlePoolsHint')}</span>
        </div>

        {poolsPending ? <p className="muted">{t('common.loading')}</p> : null}
        {poolsError ? <p className="error">{t('common.somethingWentWrong')}</p> : null}

        {pools && pools.length === 0 ? (
          <div className={styles.poolEmpty}>
            <strong>{t('pool.noPoolsYet')}</strong>
            <span className={styles.sectionHint}>{t('pool.noPoolsYetHint')}</span>
          </div>
        ) : null}

        {poolImageError ? <p className="error">{poolImageError}</p> : null}

        <input
          ref={poolImageInputRef}
          type="file"
          accept="image/*"
          className={styles.hiddenFileInput}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            const poolId = poolImageTargetRef.current;
            if (!file || !poolId) return;
            if (file.size > MAX_COVER_IMAGE_BYTES) {
              setPoolImageError(t('coverImage.tooLarge'));
              return;
            }
            setPoolImageError(null);
            updatePoolImage.mutate({ poolId, file });
          }}
        />

        {pools && pools.length > 0 ? (
          <ul className={styles.poolList}>
            {pools.map((pool) => {
              const canOpen = pool.isPlaying || group.isOrganizer;
              return (
                <li key={pool.id} className={styles.poolRow}>
                  {/* Image edit trigger sits outside the Link (like the
                      delete button below it) — a button nested in an anchor
                      either doesn't fire cleanly or fires the navigation
                      too, depending on the browser. */}
                  {group.isOrganizer ? (
                    <button
                      type="button"
                      className={styles.poolImageEdit}
                      aria-label={t('coverImage.upload')}
                      disabled={updatePoolImage.isPending}
                      onClick={() => {
                        poolImageTargetRef.current = pool.id;
                        poolImageInputRef.current?.click();
                      }}
                    >
                      {pool.imageUrl ? (
                        <img src={pool.imageUrl} alt="" className={styles.poolImage} />
                      ) : (
                        <span className={styles.poolImagePlaceholder}>+</span>
                      )}
                    </button>
                  ) : null}

                  {/* Straight to Predictions for anyone already playing —
                      the bolão's own URL is only a gate for people who still
                      have to opt in (`PoolScreen`). */}
                  <Link
                    to={
                      canOpen
                        ? `/groups/${id}/bolaos/${pool.id}/predictions`
                        : `/groups/${id}/bolaos/${pool.id}`
                    }
                    className={styles.poolCard}
                  >
                    {!group.isOrganizer && pool.imageUrl ? (
                      <img src={pool.imageUrl} alt="" className={styles.poolImage} />
                    ) : null}
                    <span className={styles.poolMain}>
                      <span className={styles.poolName}>{pool.name}</span>
                      <span className={styles.poolMeta}>
                        <CompetitionFlag slug={pool.competitionSlug} />
                        {pool.competitionDisplayName} ·{' '}
                        {t('groups.playerCount', { count: pool.participantCount })}
                      </span>
                    </span>
                    <span
                      className={`${styles.poolAction} ${canOpen ? styles.poolActionOpen : styles.poolActionJoin}`}
                    >
                      {canOpen ? t('pool.open') : t('pool.join')}
                    </span>
                  </Link>

                  {group.isOrganizer ? (
                    <button
                      type="button"
                      className={styles.poolDelete}
                      aria-label={t('pool.deleteConfirmTitle', { name: pool.name })}
                      disabled={deletePool.isPending}
                      onClick={() => {
                        if (window.confirm(t('pool.deleteConfirm', { name: pool.name }))) {
                          deletePool.mutate(pool.id);
                        }
                      }}
                    >
                      🗑
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        {group.isOrganizer ? (
          <Link
            to={`/groups/${id}/bolaos/new`}
            className={styles.poolCreate}
            style={{ marginTop: pools && pools.length > 0 ? 'var(--space-2)' : undefined }}
          >
            <span className={styles.poolCreateIcon} aria-hidden="true">
              +
            </span>
            {t('pool.createPool')}
          </Link>
        ) : null}
      </section>

      {group.isOrganizer ? (
        <section className="panel stack" style={{ padding: 'var(--space-4)' }}>
          <h2 className={styles.sectionTitle}>{t('group.share')}</h2>

          {/* Two modes, as in any document-sharing tool: a link anyone can use,
              and invitations addressed to one person. */}
          <div className="stack">
            <span className="label">{t('group.anyoneWithLink')}</span>

            {joinUrl ? (
              <div className={styles.inviteBox}>
                <code className={styles.inviteLink}>{joinUrl}</code>
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() =>
                    void navigator.clipboard.writeText(joinUrl).then(() => setCopied(true))
                  }
                >
                  {copied ? t('group.copied') : t('group.copy')}
                </button>
              </div>
            ) : (
              <p className="muted" style={{ fontSize: '0.85rem' }}>
                {t('group.linkDisabled')}
              </p>
            )}

            <button
              type="button"
              className="button button-secondary"
              disabled={regenerate.isPending}
              onClick={() => regenerate.mutate()}
            >
              {regenerate.isPending
                ? t('group.generatingLink')
                : group.linkSharingEnabled
                  ? t('group.regenerateLink')
                  : t('group.enableLink')}
            </button>
            <p className="muted" style={{ fontSize: '0.8rem' }}>
              {t('group.regenerateHint')}
            </p>
          </div>

          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              if (inviteEmail.trim() && !invite.isPending) invite.mutate();
            }}
          >
            <label className="label" htmlFor="inviteEmail">
              {t('group.inviteByEmail')}
            </label>
            <input
              id="inviteEmail"
              className="input"
              type="email"
              value={inviteEmail}
              placeholder={t('auth.emailPlaceholder')}
              onChange={(event) => setInviteEmail(event.target.value)}
            />
            {invite.isError ? (
              <p className="error">
                {invite.error instanceof ApiError
                  ? invite.error.message
                  : t('common.somethingWentWrong')}
              </p>
            ) : null}
            <button
              type="submit"
              className="button button-primary"
              disabled={!inviteEmail.trim() || invite.isPending}
            >
              {invite.isPending ? t('group.inviting') : t('group.sendInvite')}
            </button>
          </form>

          {group.pendingInvites && group.pendingInvites.length > 0 ? (
            <div className="stack">
              <span className="label">{t('group.pendingInvites')}</span>
              <ul className={styles.roster}>
                {group.pendingInvites.map((pending) => (
                  <li key={pending.id} className={styles.pendingRow}>
                    <span className="muted">{pending.email}</span>
                    <button
                      type="button"
                      className="button button-danger"
                      disabled={revoke.isPending}
                      onClick={() => revoke.mutate(pending.id)}
                    >
                      {t('group.revoke')}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : null}

      <section>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle} style={{ marginBottom: 0 }}>
            {t('group.roster')}
          </h2>
          <span className={styles.sectionHint}>
            {t('groups.playerCount', { count: group.memberCount })}
          </span>
        </div>

        {group.roster.length === 0 ? (
          <p className="muted">{t('group.noPlayers')}</p>
        ) : (
          <ul className={styles.roster}>
            {group.roster.map((player) => (
              <li key={player.id} className={`panel ${styles.rosterRow}`}>
                <Avatar name={player.displayName} imageUrl={player.avatarUrl} size={32} />
                <div className={styles.rosterMain}>
                  <span className={styles.playerName}>{player.displayName}</span>
                  <span className={styles.tags}>
                    {player.isOwner ? (
                      <span className={styles.tag}>{t('groups.organizer')}</span>
                    ) : null}
                    {player.isYou ? <span className={styles.tag}>{t('group.you')}</span> : null}
                    {/* Not a slight — it is why this person can't be emailed,
                        and why the organizer is the one who can reset their
                        PIN if they forget it. */}
                    {player.isGuest ? (
                      <span className={styles.tag}>{t('guest.noAccount')}</span>
                    ) : null}
                    {player.isGuest && !player.pinSet ? (
                      <span className={styles.tag}>{t('guest.needsPin')}</span>
                    ) : null}
                  </span>
                </div>

                <div className={styles.rosterActions}>
                  {/* The reset does not choose a PIN, it clears the old one:
                      the next person to open the invite link and pick this
                      name sets a new one. See `resetGuestPin`. */}
                  {group.isOrganizer && player.isGuest && player.pinSet ? (
                    <button
                      type="button"
                      className="button button-secondary"
                      disabled={resetPin.isPending}
                      onClick={() => {
                        if (
                          window.confirm(t('guest.confirmResetPin', { name: player.displayName }))
                        ) {
                          resetPin.mutate(player.id);
                        }
                      }}
                    >
                      {t('guest.resetPin')}
                    </button>
                  ) : null}

                  {/* The organizer cannot remove themselves; leaving a group
                      without an owner would orphan it. */}
                  {group.isOrganizer && !player.isOwner ? (
                    <button
                      type="button"
                      className="button button-danger"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (
                          window.confirm(t('group.confirmRemove', { name: player.displayName }))
                        ) {
                          remove.mutate(player.id);
                        }
                      }}
                    >
                      {t('group.remove')}
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Only for someone playing without an account, and only here: this is
          the one circle they have, so it is the one place the offer means
          anything. */}
      {isGuest ? <GuestUpgradePanel /> : null}

      {group.isOrganizer ? (
        <section className="panel stack" style={{ padding: 'var(--space-4)' }}>
          <h2 className={styles.sectionTitle}>{t('group.dangerZone')}</h2>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            {t('group.deleteGroupHint')}
          </p>
          <button
            type="button"
            className="button button-danger"
            style={{ alignSelf: 'flex-start' }}
            disabled={deleteGroup.isPending}
            onClick={() => {
              if (window.confirm(t('group.deleteGroupConfirm', { name: group.name }))) {
                deleteGroup.mutate();
              }
            }}
          >
            {deleteGroup.isPending ? t('group.deletingGroup') : t('group.deleteGroup')}
          </button>
        </section>
      ) : null}
    </main>
  );
}
